import { Capacitor } from '@capacitor/core';
import { Box, useTheme } from '@mui/material';
import { TenantCountryCodeEnum } from 'probonio-shared-ui/api';
import { useTenant } from 'probonio-shared-ui/module/me';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingLayout } from '../../../component/layout/LoadingLayout';
import { downloadFileNative } from '../../../utils/fileConversion/nativeFileDownload';
import { useBikeleasingDetails } from './useBikeleasingDetails';

interface MessageData {
  blob: Blob;
  filename: string;
}

export const BikeleasingCalculator: React.FC = () => {
  const theme = useTheme();
  const { i18n } = useTranslation();
  const backgroundColor = theme.palette.mode === 'dark' ? theme.palette.bikeleasing.secondary : theme.palette.background.paper;
  const { tenant } = useTenant();
  const bikeleasingBenefitDetails = useBikeleasingDetails();

  const calcUrl = useMemo(() => {
    const companyCode = bikeleasingBenefitDetails?.bikeleasingCondition.companyCode;
    if (!tenant || !companyCode) {
      return undefined;
    }

    const origin = import.meta.env.VITE_BIKELEASING_CALCULATOR_URL;
    if (!origin) {
      return undefined;
    }
    const isAustria = tenant.countryCode === TenantCountryCodeEnum.At;
    const url = new URL(isAustria ? '/homepage/at/calculatorat' : '/homepage/de/calculator', origin);

    if (companyCode) {
      url.searchParams.set('code', companyCode);
    }

    if (isAustria && i18n.resolvedLanguage === 'de') {
      url.searchParams.set('lang', 'de-AT');
    } else if (i18n.resolvedLanguage !== 'de') {
      url.searchParams.set('lang', 'en-US');
    }

    return url.toString();
  }, [bikeleasingBenefitDetails?.bikeleasingCondition.companyCode, tenant, i18n.resolvedLanguage]);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      const handleMessage = (e: MessageEvent) => {
        const messageData: MessageData = e.data;
        void downloadFileNative(messageData.filename, messageData.blob, 'application/pdf');
      };
      window.addEventListener('message', handleMessage, false);

      return () => {
        window.removeEventListener('message', handleMessage);
      };
    }
  }, [bikeleasingBenefitDetails]);

  if (!calcUrl) {
    return <LoadingLayout />;
  }
  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor,
      }}
    >
      <iframe
        src={calcUrl}
        seamless
        style={{
          textAlign: 'center',
          outline: 'none',
          border: 'none',
          width: '100%',
          height: '100%',
        }}
      />
    </Box>
  );
};
